import React, { ChangeEvent, useState, useEffect } from 'react';
import styled from 'styled-components';
import { LeftBracket, RightBracket } from 'kb-web-shared';
import { utils, themes, theme } from 'kb-shared';

import { ReactComponent as Placeholder } from 'assets/icons/calculator-blank.svg';
import { ResultType } from 'screens/Results/types';
import { HelperText, FooterHelperText, Star, Link } from './common';
const { calculateEggCountRange } = utils;

interface Props {
  userAge: number;
  afcResult?: ResultType;
  amhResult?: ResultType;
}

interface State {
  ageInput?: number;
  AMHLevelInput?: number;
  AFCInput?: number;
  eggCountRange?: string | null;
}

const EggCountCalculator = ({ userAge, afcResult, amhResult }: Props) => {
  const [state, setState] = useState<State>({
    ageInput: undefined,
    AMHLevelInput: undefined,
    AFCInput: undefined,
    eggCountRange: undefined
  });

  useEffect(() => {
    const ageInput = userAge;
    const AMHLevelInput = amhResult !== undefined ? parseFloat(amhResult.value) : undefined;
    const AFCInput = afcResult !== undefined ? parseInt(afcResult.value) : undefined;
    const eggCountRange =
      ageInput && AMHLevelInput && AFCInput
        ? calculateEggCountRange(ageInput, AMHLevelInput, AFCInput)
        : undefined;

    setState({
      ageInput,
      AMHLevelInput,
      AFCInput,
      eggCountRange
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAge, afcResult, amhResult]);

  const handleInputChange = (slug: string) => (e: ChangeEvent<HTMLInputElement>) => {
    // @ts-ignore
    const newValue = e.target.value ? parseFloat(e.target.value, 10) : 0;

    const ageInput = slug === 'ageInput' ? newValue : state.ageInput;
    const AMHLevelInput = slug === 'AMHLevelInput' ? newValue : state.AMHLevelInput;
    const AFCInput = slug === 'AFCInput' ? newValue : state.AFCInput;
    const eggCountRange =
      ageInput && AMHLevelInput && AFCInput
        ? calculateEggCountRange(ageInput, AMHLevelInput, AFCInput)
        : undefined;

    setState({
      ...state,
      [slug]: newValue,
      eggCountRange
    });

    if (window.analytics) {
      window.analytics.track('Egg Count Calculator Used');
    }
  };

  const { ageInput, AMHLevelInput, AFCInput, eggCountRange } = state;
  const hasEggCount = !!eggCountRange;

  return (
    <Container>
      <HelperText>
        Get a rough sense of what you can expect out of an egg freezing cycle using key data points
        you&apos;ll receive during your initial assessment.
      </HelperText>
      <FormContainer>
        <InputContainer>
          <Label>Your age</Label>
          <Input type="number" onChange={handleInputChange('ageInput')} value={ageInput || ''} />
        </InputContainer>
        <InputContainer>
          <Label>AMH Level</Label>
          <Input
            type="number"
            onChange={handleInputChange('AMHLevelInput')}
            value={AMHLevelInput || ''}
          />
        </InputContainer>
        <InputContainer>
          <Label>AFC</Label>
          <Input type="number" onChange={handleInputChange('AFCInput')} value={AFCInput || ''} />
        </InputContainer>
      </FormContainer>
      <EggCountContainer>
        {!hasEggCount && <Placeholder />}
        {hasEggCount && (
          <>
            <HelperText>Likely count of eggs retrieved</HelperText>
            <Row>
              <LeftBracket color={theme.colors.yellow} />
              <Column>
                <EggCountNumber>{eggCountRange}</EggCountNumber>
                <EggCountUnit>eggs</EggCountUnit>
              </Column>
              <RightBracket color={theme.colors.yellow} />
            </Row>
          </>
        )}
      </EggCountContainer>
      <FooterHelperText>
        <Star>*</Star>This is intended as an estimate only, and your actual number could be higher
        or lower. It&apos;s based on this{' '}
        <Link
          href="https://s3.us-east-2.amazonaws.com/kindbody-portal-static-files/egg-predictor.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          research paper
        </Link>
        .
      </FooterHelperText>
    </Container>
  );
};

export default EggCountCalculator;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;
const FormContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  justify-content: space-between;
`;
const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  &:nth-child(2) {
    align-items: center;
  }

  &:nth-child(3) {
    align-items: flex-end;
  }
`;
const Label = styled.div`
  width: 80px;
  margin-bottom: 4px;
  ${themes.fonts.FoundersGrotesk}
  font-size: 14px;
  text-transform: uppercase;
`;
const Input = styled.input`
  width: 80px;
  padding-bottom: 10px;
  border: 0;
  ${themes.fonts.DomaineDisplay}
  font-size: 24px;
  border-bottom: 1px solid black;
  background-color: transparent;
  text-align: left;
`;
const EggCountContainer = styled.div`
  margin-top: 28px;
  padding: 12px 16px;
  box-shadow: -4px 4px 0 0;
  border: solid 2px ${themes.colors.night};
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & ${HelperText} {
    margin-top: 0;
    text-align: center;
  }
`;
const Row = styled.div`
  padding-bottom: 12px;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;
const Column = styled.div`
  padding: 13px 7px 8px;
  display: flex;
  flex-direction: column;
`;
const EggCountNumber = styled.div`
  ${themes.fonts.FoundersGrotesk};
  color: ${themes.colors.night};
  font-size: 48px;
  font-weight: 300;
  line-height: 0.5;
  text-align: center;
`;
const EggCountUnit = styled.div`
  margin-top: 22px;
  ${themes.fonts.FoundersGrotesk};
  color: ${themes.colors.night};
  font-size: 16px;
  text-align: center;
`;
